@file:OptIn(ExperimentalLayoutApi::class)

package music.jammers.client.shared.event.track.presentation.ui

import androidx.compose.foundation.ScrollState
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Close
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material.icons.filled.MoreVert
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardCapitalization
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import coil3.compose.AsyncImage
import kotlinx.coroutines.launch
import limasoul.client.shared.generated.resources.Res
import limasoul.client.shared.generated.resources.create_event_track_screen_comment
import limasoul.client.shared.generated.resources.create_event_track_screen_members
import limasoul.client.shared.generated.resources.event_detail_screen_optional_warning
import limasoul.client.shared.generated.resources.event_track_details_screen_added_by
import limasoul.client.shared.generated.resources.event_track_details_screen_delete_button
import limasoul.client.shared.generated.resources.event_track_details_screen_delete_confirmation_message
import limasoul.client.shared.generated.resources.event_track_details_screen_delete_confirmation_title
import limasoul.client.shared.generated.resources.event_track_details_screen_ready_members
import limasoul.client.shared.generated.resources.event_track_details_screen_required_members
import limasoul.client.shared.generated.resources.event_track_details_screen_title
import music.jammers.client.shared.component.AppAutoLinkClickableText
import music.jammers.client.shared.component.AppTextField
import music.jammers.client.shared.component.BackgroundBox
import music.jammers.client.shared.component.ConfirmationDialog
import music.jammers.client.shared.component.ErrorDialog
import music.jammers.client.shared.component.FieldValidationState
import music.jammers.client.shared.component.JammersTopAppBar
import music.jammers.client.shared.component.MemberInstrumentImage
import music.jammers.client.shared.component.Progress
import music.jammers.client.shared.component.getInstrumentByType
import music.jammers.client.shared.component.member.info.legend.presentation.ui.MemberInfoLegendComponent
import music.jammers.client.shared.component.optionalInstrumentColor
import music.jammers.client.shared.event.domain.usecase.ValidateEventUseCase
import music.jammers.client.shared.event.presentation.ui.ConfirmAcceptMemberDialog
import music.jammers.client.shared.event.presentation.ui.ConfirmCancelMemberDialog
import music.jammers.client.shared.event.presentation.ui.getString
import music.jammers.client.shared.event.track.di.EventTrackComponent
import music.jammers.client.shared.event.track.presentation.viewmodel.EventTrackDetailsState
import music.jammers.client.shared.event.track.presentation.viewmodel.EventTrackDetailsViewModel
import music.jammers.shared.event.domain.model.EventMember
import music.jammers.shared.user.domain.model.User
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

const val EVENT_TRACK_DETAILS_SCREEN = "EventTrackDetailsScreen"

@Composable
fun EventTrackDetailsScreen(eventId: Long, eventTrackId: Long) {
    val viewModel = remember {
        EventTrackComponent.eventTrackDetailsViewModel.apply {
            onBind(eventId, eventTrackId)
        }
    }

    val uriHandler = LocalUriHandler.current
    LaunchedEffect(Unit) { viewModel.openUrlEvent.collect(uriHandler::openUri) }

    val state = viewModel.state.value
    val validationState = viewModel.validationState.value
    val editMode = viewModel.editModeState.value

    val snackbarHostState = remember { SnackbarHostState() }

    val snackbarMessage = stringResource(Res.string.event_detail_screen_optional_warning)

    LaunchedEffect(Unit) {
        viewModel.snackBarState.collect {
            launch {
                snackbarHostState.showSnackbar(snackbarMessage)
            }
        }
    }

    Scaffold(
        topBar = {
            JammersTopAppBar(
                text = stringResource(Res.string.event_track_details_screen_title),
                navigationIcon = {
                    if (editMode) {
                        IconButton(onClick = { viewModel.onCloseEditModeClicked() }) {
                            Icon(
                                imageVector = Icons.Filled.Close,
                                contentDescription = "Close",
                            )
                        }
                    } else {
                        IconButton(onClick = { viewModel.onBackPressed() }) {
                            Icon(
                                imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                                contentDescription = "Back",
                            )
                        }
                    }
                },
                actions = {
                    if (editMode) {
                        IconButton(onClick = {
                            viewModel.onApplyClicked()
                        }) {
                            Icon(
                                imageVector = Icons.Filled.Check,
                                contentDescription = "Apply",
                            )
                        }
                    } else if (state.isAdminOrOwner()) {
                        IconButton(onClick = {
                            viewModel.onEditModeClicked()
                        }) {
                            Icon(
                                imageVector = Icons.Filled.Edit,
                                contentDescription = "Edit",
                            )
                        }
                    }

                    if (state.isAdminOrOwner()) {
                        var showMenu by remember { mutableStateOf(false) }

                        IconButton(onClick = { showMenu = true }) {
                            Icon(Icons.Filled.MoreVert, contentDescription = "Menu")
                        }

                        DropdownMenu(expanded = showMenu, onDismissRequest = { showMenu = false }) {
                            DropdownMenuItem(text = {
                                Text(stringResource(Res.string.event_track_details_screen_delete_button))
                            }, onClick = {
                                showMenu = false
                                viewModel.onDeleteTrackClicked()
                            })
                        }
                    }
                },
            )
        },
        snackbarHost = {
            SnackbarHost(snackbarHostState)
        },
    ) { paddingValues: PaddingValues ->
        BackgroundBox {
            val scrollState = rememberScrollState()

            if (state.progress) {
                Progress()
            } else {
                Column(
                    modifier = Modifier.padding(paddingValues).padding(horizontal = 22.dp),
                ) {
                    Content(state, validationState, editMode, viewModel, scrollState)
                }
            }
        }
    }
}

@Composable
private fun Content(
    state: EventTrackDetailsState,
    validationState: FieldValidationState,
    editMode: Boolean,
    viewModel: EventTrackDetailsViewModel,
    scrollState: ScrollState,
) {
    state.error?.let { error ->
        ErrorDialog(error.message ?: "error") {
            viewModel.onErrorShown()
        }
    }

    state.confirmSignInTrack?.let { eventMember ->
        ConfirmAcceptMemberDialog(
            trackName = state.trackTitle,
            artistName = state.artistName,
            instrumentName = eventMember.instrumentType.instrument.name,
            onPositiveClicked = {
                viewModel.onSignInPositive(eventMember)
            },
            onNegativeClicked = {
                viewModel.onDialogNegative()
            }
        )
    }

    state.confirmSignOutTrack?.let { eventMember ->
        ConfirmCancelMemberDialog(
            trackName = state.trackTitle,
            artistName = state.artistName,
            instrumentName = eventMember.instrumentType.instrument.name,
            onPositiveClicked = {
                viewModel.onSignOutPositive(eventMember)
            },
            onNegativeClicked = {
                viewModel.onDialogNegative()
            }
        )
    }

    if (state.deleteDialogOpened) {
        ConfirmationDialog(
            Res.string.event_track_details_screen_delete_confirmation_title,
            Res.string.event_track_details_screen_delete_confirmation_message,
            onPositiveClicked = viewModel::onDeleteTrackDialogOkPressed,
            onNegativeClicked = viewModel::onDeleteTrackDialogCancelButtonClicked
        )
    }

    Column(
        modifier = Modifier.padding(16.dp).verticalScroll(scrollState)
    ) {
        SelectTrackContent(state, onUserClick = viewModel::onUserClicked)
        MembersContent(state, validationState, editMode, viewModel)
        AdditionalContent(state, editMode, viewModel)
    }
}

@Composable
private fun SelectTrackContent(
    state: EventTrackDetailsState,
    onUserClick: (User) -> Unit,
) {
    Text(
        text = state.artistName.uppercase(),
        style = MaterialTheme.typography.headlineMedium,
        modifier = Modifier.fillMaxWidth(),
    )

    Text(
        text = state.trackTitle,
        style = MaterialTheme.typography.headlineMedium,
        modifier = Modifier.fillMaxWidth(),
    )

    Spacer(modifier = Modifier.size(6.dp))

    state.addedBy?.let { user ->
        val text = user.telegram.ifEmpty { user.name }
        Text(
            text = stringResource(
                Res.string.event_track_details_screen_added_by,
                text,
            ),
            style = MaterialTheme.typography.bodyLarge,
            modifier = Modifier.fillMaxWidth()
                .clickable {
                    onUserClick(user)
                },
        )
    }
}

@Composable
private fun AdditionalContent(
    state: EventTrackDetailsState,
    editMode: Boolean,
    viewModel: EventTrackDetailsViewModel,
) {
    if (editMode) {
        AppTextField(
            value = state.comment,
            placeholder = { Text(stringResource(Res.string.create_event_track_screen_comment)) },
            enabled = true,
            keyboardOptions = KeyboardOptions(
                capitalization = KeyboardCapitalization.Sentences,
                autoCorrectEnabled = true,
                keyboardType = KeyboardType.Text,
                imeAction = ImeAction.Next,
            ),
            onValueChange = { value ->
                viewModel.onCommentChanged(value)
            },
            modifier = Modifier.fillMaxWidth(),
        )
    } else {
        if (state.comment.isNotEmpty()) {
            Spacer(modifier = Modifier.size(12.dp))

            AppAutoLinkClickableText(
                modifier = Modifier.fillMaxWidth(),
                text = state.comment,
                onClick = viewModel::onUrlClick
            )
        }
    }
}

@Composable
private fun MembersContent(
    state: EventTrackDetailsState,
    validationState: FieldValidationState,
    editMode: Boolean,
    viewModel: EventTrackDetailsViewModel,
) {
    Spacer(modifier = Modifier.size(24.dp))

    Row(
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.fillMaxWidth()
    ) {
        Text(
            text = stringResource(Res.string.create_event_track_screen_members).uppercase(),
            style = MaterialTheme.typography.headlineSmall,
        )

        MemberInfoLegendComponent(editMode)
    }

    if (editMode) {
        EditMembers(
            state,
            validationState,
            viewModel,
        )
    } else {
        ReadyMembers(
            members = state.readyMembers(),
            onMemberClick = { eventMember ->
                viewModel.onInstrumentClicked(eventMember)
            },
            onUserClick = { user ->
                viewModel.onUserClicked(user)
            }
        )

        Spacer(modifier = Modifier.size(8.dp))

        RequiredMembers(state.requiredMembers()) { eventMember ->
            viewModel.onInstrumentClicked(eventMember)
        }
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun EditMembers(
    state: EventTrackDetailsState,
    validationState: FieldValidationState,
    viewModel: EventTrackDetailsViewModel,
) {
    FlowRow(modifier = Modifier) {
        state.selectedInstruments.forEach { item ->
            UpdateInstrumentImage(
                updateInstrument = item,
                onClick = viewModel::onInstrumentClicked,
            )
            Spacer(Modifier.size(8.dp))
        }
    }

    val selectedInstrumentError = validationState.getValidationError(
        ValidateEventUseCase.SELECTED_INSTRUMENTS
    ).getString()

    if (selectedInstrumentError == null) {
        Spacer(modifier = Modifier.size(16.dp))
    } else {
        Text(
            text = selectedInstrumentError,
            style = MaterialTheme.typography.labelMedium,
            maxLines = 1,
        )
    }
}

@Composable
private fun MemberItem(
    member: EventMember,
    onMemberClick: (EventMember) -> Unit,
    onUserClick: (User) -> Unit,
) {
    member.user?.let { user ->
        Row {
            val instrument = getInstrumentByType(member.instrumentType.instrument)
            val icon = instrument.icon

            val isOptional = member.status == EventMember.Status.BOOKED_OPTIONAL

            val tintColor = if (isOptional) {
                MaterialTheme.colorScheme.primary
            } else {
                instrument.iconColor
            }

            val backgroundColor = if (isOptional) {
                optionalInstrumentColor
            } else {
                MaterialTheme.colorScheme.secondaryContainer
            }

            Box(
                modifier = Modifier.size(70.dp)
            ) {
                Box(
                    modifier = Modifier.size(60.dp)
                        .clip(shape = CircleShape)
                        .clickable {
                            onMemberClick(member)
                        }
                        .background(
                            color = backgroundColor,
                        )
                        .align(Alignment.BottomStart)
                ) {
                    Icon(
                        painter = painterResource(icon),
                        tint = tintColor,
                        contentDescription = "Instrument",
                        modifier = Modifier.matchParentSize().padding(10.dp),
                    )
                }

                AsyncImage(
                    model = user.pictureUrl,
                    contentDescription = "user_picture",
                    contentScale = ContentScale.Crop,
                    modifier = Modifier.size(30.dp).clip(
                        shape = CircleShape
                    ).align(Alignment.TopEnd),
                )
            }

            Spacer(modifier = Modifier.size(16.dp))

            Column(
                verticalArrangement = Arrangement.Center,
                modifier = Modifier.clickable {
                    onUserClick(user)
                }
            ) {
                Spacer(modifier = Modifier.size(13.dp))

                Text(
                    text = user.name,
                    style = MaterialTheme.typography.titleLarge,
                )

                if (user.telegram.isNotEmpty()) {
                    Text(
                        text = user.telegram,
                        style = MaterialTheme.typography.bodyLarge,
                    )
                }
            }
        }
    }
}

@Composable
private fun ReadyMembers(
    members: List<EventMember>,
    onMemberClick: (EventMember) -> Unit,
    onUserClick: (User) -> Unit,
) {
    Text(
        text = stringResource(Res.string.event_track_details_screen_ready_members).uppercase(),
        style = MaterialTheme.typography.bodyLarge,
        textAlign = TextAlign.Center,
        modifier = Modifier.fillMaxWidth().padding(horizontal = 8.dp),
    )

    members.forEach { item ->
        MemberItem(
            item,
            onMemberClick = onMemberClick,
            onUserClick = onUserClick,
        )
    }
}

@OptIn(ExperimentalLayoutApi::class)
@Composable
private fun RequiredMembers(
    requiredMembers: List<EventMember>,
    onClick: (EventMember) -> Unit,
) {
    Text(
        text = stringResource(Res.string.event_track_details_screen_required_members).uppercase(),
        style = MaterialTheme.typography.bodyLarge,
        textAlign = TextAlign.Center,
        modifier = Modifier.fillMaxWidth().padding(8.dp),
    )

    FlowRow(modifier = Modifier) {
        requiredMembers.forEach { item ->
            MemberInstrumentImage(
                eventMember = item,
                onClick = onClick,
            )
        }

        Spacer(Modifier.size(8.dp))
    }

    Spacer(modifier = Modifier.size(16.dp))
}